/**
 * The log list: virtualised, infinitely paged, and updating live.
 *
 * Columns are `time · server · type · sender · message`. The sender column is load-bearing —
 * `debug_logs.player` is the authoritative sender for every row, and chat messages carry no name in
 * the message body at all, so a list without this column renders every chat line anonymous.
 *
 * Three other things here are easy to get wrong:
 *
 *  1. **Only visible rows exist in the DOM.** TanStack Virtual renders a window; the buffer can hold
 *     five thousand rows and the browser is asked to lay out about forty.
 *
 *  2. **The viewport is never yanked.** Auto-scroll only happens when the user is already at the top.
 *     The moment they scroll away to read something, new rows stop moving the page and a "N new"
 *     pill appears instead.
 *
 *  3. **The buffer is capped.** An all-day tab is the normal case, not the edge case.
 */

import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { LogRow } from "../types";
import { formatTimestamp, stripLeadingPlayer, typeColor } from "../format";

/**
 * Type labels shortened to fit the column beside the colour swatch. The full name is on the
 * element's title attribute, and the swatch colour is the same either way, so nothing is lost —
 * the alternative was a column wide enough for SERVER_MOVEMENT, which is dead space on every row.
 */
const SHORT_TYPES: Readonly<Record<string, string>> = {
  SERVER_MOVEMENT: "MOVE",
  CHAT_FILTER: "FILTER",
  PUNISHMENT: "PUNISH",
  SPAWNER_STASH: "STASH",
  WAGER_DEBUG: "WAGER DBG",
};

/** Types whose rows open a detail card when clicked. Every other type renders exactly as before. */
const OPENABLE_TYPES: ReadonlySet<string> = new Set(["SPAWNER_STASH"]);

function shortType(type: string): string {
  return SHORT_TYPES[type] ?? type;
}

/** Rows kept in memory. Beyond this the oldest are dropped — they are one keyset page away if
 *  anybody scrolls back for them. */
const MAX_BUFFER = 5000;

/** How long live rows are gathered before being added, so a burst moves the list once, smoothly. */
const LIVE_BATCH_MS = 220;

/** Rows from the bottom of the list at which the next page is fetched. */
const PREFETCH_ROWS = 20;

interface Props {
  rows: LogRow[];
  /** Ids that arrived over the live stream this session, so they can be flashed once. */
  liveIds: ReadonlySet<number>;
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  onOpenPlayer: (player: string) => void;
  /** Opens the detail card for a row of an OPENABLE_TYPES type. */
  onOpenRow: (row: LogRow) => void;
  pendingCount: number;
  onResume: () => void;
  showServer: boolean;
  flashNew: boolean;
  rowHeight: number;
}

export function LogList({
  rows,
  liveIds,
  hasMore,
  loading,
  onLoadMore,
  onOpenPlayer,
  onOpenRow,
  pendingCount,
  onResume,
  showServer,
  flashNew,
  rowHeight,
}: Props): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(-1);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
    // Keyed by row id rather than index so prepending live rows does not make React tear down and
    // rebuild every visible row.
    getItemKey: (index) => rows[index]?.id ?? index,
  });

  // Density can change while the list is open; the virtualiser caches measurements and would keep
  // using the old height until something else invalidated them.
  useEffect(() => {
    virtualizer.measure();
  }, [rowHeight, virtualizer]);

  const items = virtualizer.getVirtualItems();

  useEffect(() => {
    const last = items[items.length - 1];
    if (!last) return;
    if (last.index >= rows.length - PREFETCH_ROWS && hasMore && !loading) onLoadMore();
  }, [items, rows.length, hasMore, loading, onLoadMore]);

  // j / k move through rows the way they do in less and in a mail client. Ignored while a text field
  // has focus, or the search box would be unusable.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (event.key !== "j" && event.key !== "k") return;

      event.preventDefault();
      setFocused((current) => {
        const next =
          event.key === "j"
            ? Math.min(current + 1, rows.length - 1)
            : Math.max(current - 1, 0);
        virtualizer.scrollToIndex(next, { align: "auto" });
        return next;
      });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rows.length, virtualizer]);

  if (rows.length === 0 && !loading) {
    return <div className="empty">No log entries match these filters.</div>;
  }

  return (
    <div className="list-wrap">
      {pendingCount > 0 && (
        <button type="button" className="newpill" onClick={onResume}>
          {pendingCount.toLocaleString()} new {pendingCount === 1 ? "event" : "events"} &uarr;
        </button>
      )}

      <div className="list" ref={scrollRef} role="log" aria-live="off">
        <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {items.map((item) => {
            const row = rows[item.index];
            if (!row) return null;
            const body = stripLeadingPlayer(row.message, row.player);
            const openable = OPENABLE_TYPES.has(row.type);
            return (
              <div
                key={item.key}
                className="row"
                data-new={flashNew && liveIds.has(row.id) ? "true" : undefined}
                data-focused={item.index === focused ? "true" : undefined}
                data-openable={openable ? "true" : undefined}
                {...(openable
                  ? {
                      role: "button",
                      tabIndex: 0,
                      title: "Open stash details",
                      onClick: () => onOpenRow(row),
                      onKeyDown: (event: ReactKeyboardEvent) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onOpenRow(row);
                        }
                      },
                    }
                  : {})}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: item.size,
                  transform: `translateY(${item.start}px)`,
                  // Set per row so the type swatch and label share one colour without a class per type.
                  ["--chip" as string]: typeColor(row.type),
                }}
              >
                <span className="c-ts">{formatTimestamp(row.logged_at)}</span>
                {showServer && <span className="c-srv">{row.server}</span>}
                <span className="c-type" title={row.type}>
                  <i />
                  <span>{shortType(row.type)}</span>
                </span>
                <span className="c-who">
                  {row.player ? (
                    <button
                      type="button"
                      className="who"
                      onClick={(event) => {
                        // An openable row has its own click; the name still means "open the player".
                        event.stopPropagation();
                        onOpenPlayer(row.player as string);
                      }}
                    >
                      {row.player}
                    </button>
                  ) : (
                    <span className="who-none">—</span>
                  )}
                </span>
                <span className="c-msg" title={row.message}>
                  {body}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Owns the row buffer: the paged history, live arrivals, the cap, and the pause-on-scroll rule.
 *
 * Split out from the component because the interesting part of a live list is not how it renders but
 * what it decides to keep and when it decides to move.
 */
export function useLogBuffer(): {
  rows: LogRow[];
  liveIds: ReadonlySet<number>;
  pending: LogRow[];
  paused: boolean;
  setPaused: (paused: boolean) => void;
  pushLive: (row: LogRow) => void;
  appendPage: (rows: LogRow[]) => void;
  replaceAll: (rows: LogRow[]) => void;
  flushPending: () => void;
} {
  const [rows, setRows] = useState<LogRow[]>([]);
  const [pending, setPending] = useState<LogRow[]>([]);
  const [paused, setPaused] = useState(false);
  const liveIds = useRef<Set<number>>(new Set());

  /**
   * Live rows wait here for a moment and are added together. Adding each one the instant it arrives
   * re-renders and shifts the whole list once per event, which on a busy network is a stutter of
   * back-to-back jumps. One batch every LIVE_BATCH_MS is a single glide (see `.list .row` in the
   * stylesheet, which animates the shift).
   */
  const queue = useRef<LogRow[]>([]);
  const flushTimer = useRef<number | null>(null);

  const trim = (next: LogRow[]): LogRow[] =>
    next.length > MAX_BUFFER ? next.slice(0, MAX_BUFFER) : next;

  useEffect(
    () => () => {
      if (flushTimer.current !== null) window.clearTimeout(flushTimer.current);
    },
    [],
  );

  const flushQueue = useCallback(() => {
    flushTimer.current = null;
    const batch = queue.current;
    queue.current = [];
    if (batch.length === 0) return;
    setRows((current) => {
      // The reconnect replay can overlap with rows already fetched, so a live arrival that is
      // already present is dropped rather than duplicated.
      const seen = new Set(current.map((existing) => existing.id));
      const fresh = batch.filter((row) => !seen.has(row.id)).reverse();
      return fresh.length === 0 ? current : trim([...fresh, ...current]);
    });
  }, []);

  const pushLive = useCallback(
    (row: LogRow) => {
      liveIds.current.add(row.id);
      if (liveIds.current.size > MAX_BUFFER) {
        liveIds.current = new Set([...liveIds.current].slice(-MAX_BUFFER));
      }

      if (paused) {
        setPending((current) => trim([row, ...current]));
        return;
      }
      queue.current.push(row);
      if (flushTimer.current === null) {
        flushTimer.current = window.setTimeout(flushQueue, LIVE_BATCH_MS);
      }
    },
    [paused, flushQueue],
  );

  const appendPage = useCallback((page: LogRow[]) => {
    setRows((current) => {
      const seen = new Set(current.map((row) => row.id));
      return trim([...current, ...page.filter((row) => !seen.has(row.id))]);
    });
  }, []);

  const replaceAll = useCallback((next: LogRow[]) => {
    liveIds.current = new Set();
    // Anything still waiting belongs to the previous filter.
    queue.current = [];
    setPending([]);
    setRows(trim(next));
  }, []);

  const flushPending = useCallback(() => {
    setRows((current) => {
      const seen = new Set(current.map((row) => row.id));
      return trim([...pending.filter((row) => !seen.has(row.id)), ...current]);
    });
    setPending([]);
  }, [pending]);

  return {
    rows,
    liveIds: liveIds.current,
    pending,
    paused,
    setPaused,
    pushLive,
    appendPage,
    replaceAll,
    flushPending,
  };
}
